import { listTagsQuery, namespacesListQuery } from 'apis/wetags/queriesV2';
import { WETAGS } from 'constants/paths';
import { useQueries, useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { Link, Route, useLocation } from 'react-router-dom';
import { CrawlSwitch } from 'routers/crawl-switch';
import {
  PrimaryButton,
  Text,
  TextField,
  TextLink,
  useFormField,
} from '@weave/design-system';
import { ListTagsResponse } from '@weave/schema-gen-ts/dist/apis/api/wetags/v1/api.pb';
import { CreateNamespaceModal } from './namespace/CreateNamespaceModal.component';
import { ComponentProps, useMemo, useState } from 'react';
import { CreateTagModal } from './tag/CreateTagModal.component';
import { WetagsNamespaceRoot } from './namespace/NamespaceRoot.component';
import { WetagsTagRoot } from './tag/TagRoot.component';
import { getRecentlyAccessedNamespaces } from './namespace/namespaceStorage';
import { Page } from 'components/shared';
import { getBreadcrumbs } from './wetagsBreadcrumbs';
import { List, ListItem, ListRenderItemOptions } from './components/List.component';
import { WetagsHeader } from './components/WetagsHeader.component';
import { theme } from '@weave/theme-original';

/**
 * The root container of Wetags pages
 */
export function WetagsRoot() {
  return (
    <section>
      <CrawlSwitch>
        <Route exact path={WETAGS.root} component={WetagsMain} />
        <Route exact path={WETAGS.namespace} component={WetagsNamespaceRoot} />
        <Route exact path={WETAGS.tag} component={WetagsTagRoot} />
      </CrawlSwitch>
    </section>
  );
}

function WetagsMain() {
  const [isCreateNamespaceModalOpen, setIsCreateNamespaceModalOpen] = useState(false);
  const [isCreateTagModalOpen, setIsCreateTagModalOpen] = useState(false);
  const location = useLocation();
  const recentlyAccessedNamespaces = getRecentlyAccessedNamespaces();
  const tagSearchField = useFormField({ type: 'text' });
  const tagSearchTerm = tagSearchField.value.trim().toLowerCase();
  const canSearchTags = tagSearchTerm.length > 2;

  const { data = { namespaces: [] }, isLoading } = useQuery(
    namespacesListQuery.getUseQueryOptions({
      pageSize: 500,
    })
  );

  const sortedNamespaces = useMemo(
    () =>
      data.namespaces
        ? data.namespaces.sort((a, b) => (a.id && b.id ? a.id.localeCompare(b.id) : 0))
        : [],
    [data.namespaces]
  );

  /**
   * This is unfortunately the only way we can perform a tag search across all namespaces right now.
   * Once we have proper endpoints for searching across namespaces, we can replace this approach.
   */
  const tagQueries = useQueries(
    sortedNamespaces.map((namespace) =>
      listTagsQuery.getUseQueryOptions(
        {
          namespaceId: namespace.id ?? '',
          pageSize: 500,
        },
        { enabled: canSearchTags }
      )
    ) as UseQueryOptions[]
  ) as UseQueryResult<ListTagsResponse>[];
  const isTagSearchPending = isLoading || tagQueries.some((query) => query.isLoading);
  const matchingTags = useMemo(
    () =>
      tagQueries.flatMap((query, index) => {
        const namespaceId = sortedNamespaces[index]?.id ?? '';
        return (query.data?.tags ?? [])
          .filter(
            (tag) =>
              tag.id?.toLowerCase().includes(tagSearchTerm) ||
              tag.description?.toLowerCase().includes(tagSearchTerm)
          )
          .map((tag) => ({
            id: `${namespaceId}/${tag.id}`,
            label: tag.id ?? '',
            namespaceId,
            description: tag.description,
          }));
      }),
    [sortedNamespaces, tagQueries, tagSearchTerm]
  );

  return (
    <Page
      title="Wetags 🏷️"
      subtitle="Feature release management that doesn't break"
      breadcrumbs={getBreadcrumbs(location.pathname)}
      headerActions={
        <WetagsHeader>
          <PrimaryButton
            onClick={() => setIsCreateNamespaceModalOpen(true)}
            style={{ width: 'auto', maxWidth: 'max-content' }}
          >
            Create Namespace
          </PrimaryButton>
          <PrimaryButton
            onClick={() => setIsCreateTagModalOpen(true)}
            style={{ width: 'auto', maxWidth: 'max-content' }}
          >
            Create Tag
          </PrimaryButton>
        </WetagsHeader>
      }
    >
      <section
        style={{
          marginBottom: theme.spacing(4),
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1),
        }}
      >
        <div style={{ maxWidth: 500 }}>
          <TextField {...tagSearchField} label="Search tags" name="search-tags" />
        </div>

        {canSearchTags && (
          <div
            style={{
              background: theme.colors.gray100,
              borderRadius: theme.borderRadius.medium,
              border: `1px solid ${theme.colors.gray300}`,
              padding: theme.spacing(1),
            }}
          >
            {isTagSearchPending ? (
              <Text>Searching tags...</Text>
            ) : (
              <List
                title="Matching Tags"
                width="100%"
                maxHeight={130}
                list={matchingTags.map((tag) => ({ id: tag.id, label: tag.label }))}
                renderItem={(item, options) => {
                  const tag = matchingTags.find(
                    (matchingTag) => matchingTag.id === item.id
                  );
                  return tag ? (
                    <TagSearchListItem
                      key={item.id}
                      tag={tag}
                      listItemProps={options.listItemProps}
                      focusableProps={options.focusableProps}
                    />
                  ) : null;
                }}
              />
            )}
          </div>
        )}
      </section>

      <div
        style={{
          display: 'grid',
          gap: theme.spacing(4),
          gridTemplateColumns: 'auto auto',
          justifyContent: 'start',
        }}
      >
        {/* Recently accessed namespaces */}
        {recentlyAccessedNamespaces.length > 0 && (
          <List
            title="Recently Accessed Namespaces"
            list={recentlyAccessedNamespaces.map((ns) => ({ id: ns, label: ns }))}
            renderItem={(item, options) => (
              <NamespaceListItem
                key={item.id}
                namespaceId={item.id}
                listItemProps={options.listItemProps}
                focusableProps={options.focusableProps}
              />
            )}
          />
        )}

        {/* List of all namespaces from backend */}
        {!isLoading && (
          <List
            canSearch
            title="All Namespaces"
            list={sortedNamespaces.map((ns) => ({ id: ns.id ?? '', label: ns.id ?? '' }))}
            renderItem={(item, options) => (
              <NamespaceListItem
                key={item.id}
                namespaceId={item.id}
                listItemProps={options.listItemProps}
                focusableProps={options.focusableProps}
              />
            )}
          />
        )}
      </div>

      <CreateNamespaceModal
        isOpen={isCreateNamespaceModalOpen}
        onClose={() => setIsCreateNamespaceModalOpen(false)}
      />
      <CreateTagModal
        isOpen={isCreateTagModalOpen}
        onClose={() => setIsCreateTagModalOpen(false)}
      />
    </Page>
  );
}

function NamespaceListItem({
  namespaceId,
  listItemProps,
  focusableProps,
}: {
  namespaceId: string;
  listItemProps: ComponentProps<'li'> & { isActive?: boolean };
  focusableProps: ListRenderItemOptions['focusableProps'];
}) {
  const prefetchTags = () => {
    listTagsQuery.prefetch({ namespaceId });
  };

  const deferPrefetchTags = () => {
    window.requestAnimationFrame(() => {
      prefetchTags();
    });
  };

  return (
    <ListItem
      {...listItemProps}
      onMouseEnter={(event) => {
        prefetchTags();
        listItemProps.onMouseEnter?.(event);
      }}
    >
      <Link
        component={TextLink}
        ref={focusableProps.ref}
        to={`/wetags/namespace/${namespaceId}`}
        onFocus={() => {
          focusableProps.onFocus();
          deferPrefetchTags();
        }}
        onKeyDown={focusableProps.onKeyDown}
        tabIndex={focusableProps.tabIndex}
        style={{ display: 'block', width: '100%' }}
      >
        {namespaceId}
      </Link>
    </ListItem>
  );
}

function TagSearchListItem({
  tag,
  listItemProps,
  focusableProps,
}: {
  tag: {
    id: string;
    label: string;
    namespaceId: string;
    description?: string;
  };
  listItemProps: ComponentProps<'li'> & { isActive?: boolean };
  focusableProps: ListRenderItemOptions['focusableProps'];
}) {
  return (
    <ListItem {...listItemProps}>
      <Link
        component={TextLink}
        ref={focusableProps.ref}
        to={`/wetags/namespace/${tag.namespaceId}/tag/${tag.label}`}
        onFocus={focusableProps.onFocus}
        onKeyDown={focusableProps.onKeyDown}
        tabIndex={focusableProps.tabIndex}
        style={{ display: 'block', width: '100%' }}
      >
        <Text
          size="small"
          color="primary"
          css={{
            margin: 0,
            fontSize: '0.5rem',
            fontWeight: 'bold',
            textTransform: 'uppercase',
          }}
        >
          {tag.namespaceId}
        </Text>
        <Text size="large" css={{ margin: 0 }}>
          {tag.label}
        </Text>
        {tag.description && (
          <Text size="small" color="light" css={{ margin: 0 }}>
            {tag.description}
          </Text>
        )}
      </Link>
    </ListItem>
  );
}
